import type { ScrollView } from "@-/com.npmjs.react-native";
import { HeaderScrollView } from "@-/dev.improve.haniverse.components.ui.header-scroll";
import {
  createSectionAccordionViewport,
  SectionAccordionViewportContext,
} from "@-/dev.improve.haniverse.components.ui.section-accordion";
import { DeckSectionScrollContext } from "@-/dev.improve.haniverse.routes.nav.app.deck._components.deck-card-section.viewport";
import { PrimaryNavigationStripFadeInsetContext } from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { useCallback, useContext, useRef, useState, type ReactNode } from "react";

/**
 * Page scroller for a column of `DeckCardSurface`s. Publishes the scroll geometry that each
 * surface's row windowing and detail paging read, and owns the offset correction the list view
 * applies when measured rows resize. The last surface rests above the tab bar's fade.
 */
export function DeckCardSurfaceScrollView({
  children,
  testID,
}: {
  children: ReactNode;
  testID?: string;
}) {
  const [viewport] = useState(createSectionAccordionViewport);
  const scrollRef = useRef<ScrollView>(null);
  const bottomTabFadeInset = useContext(PrimaryNavigationStripFadeInsetContext);
  const correctOffset = useCallback(
    (y: number) => scrollRef.current?.scrollTo({ animated: false, y }),
    [],
  );
  return (
    <DeckSectionScrollContext value={correctOffset}>
      <SectionAccordionViewportContext value={viewport}>
        <HeaderScrollView
          // Match the deck toolbar's chrome behind the independently painted card surfaces.
          className="min-h-0 flex-1 bg-muted"
          contentContainerStyle={{ paddingBottom: bottomTabFadeInset }}
          contentInsetAdjustmentBehavior="never"
          onLayout={({ nativeEvent: { layout } }) => {
            viewport.setViewport(Math.max(0, layout.height - bottomTabFadeInset));
          }}
          onScroll={({ nativeEvent }) => {
            viewport.setViewport(
              Math.max(0, nativeEvent.layoutMeasurement.height - bottomTabFadeInset),
              nativeEvent.contentOffset.y,
            );
          }}
          ref={scrollRef}
          scrollEventThrottle={16}
          scrollIndicatorInsets={{ bottom: bottomTabFadeInset }}
          testID={testID}
        >
          {children}
        </HeaderScrollView>
      </SectionAccordionViewportContext>
    </DeckSectionScrollContext>
  );
}
